import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Button, Card, Checkbox, FormGroup, PageHeader } from "@cofano/cck";
import {
	CircularProgress,
	Grid2 as Grid,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";
import CallTypeSelect from "components/selects/CallTypeSelect";

import { DataFormat, hasPluginPermission, Language, usePermission } from "imports/Utils";

import {
	convertStatusToMutationInput,
	getStatusFieldVisibility,
	OnlyStatusFlags,
} from "view/statuses/utils/StatusUtils";

import StatusTypeSelect from "./components/selects/StatusTypeSelect";
import DefaultStatusSettingsDialog from "./components/dialogs/DefaultStatusSettingsDialog";
import ChangeStatusOrderDialog from "./components/dialogs/ChangeStatusOrderDialog";

import { CallType, StatusActualTimeType, StatusType, StatusEstimateRequirement } from "__generated__/enums";
import { GetAllStatusesDocument, GetAllStatusesQuery, GetStatusRuleTypesDocument, GetStatusRuleTypesQuery } from "__generated__/view/statuses/graphql/queries.generated";
import { DeleteStatusDocument, DeleteStatusMutation, SaveStatusesDocument, SaveStatusesMutation } from "__generated__/view/statuses/graphql/mutations.generated";
import { StatusFragmentFragment as StatusFragment } from "__generated__/view/statuses/graphql/fragments.generated";

import "./styling/styling.scss";

const Statuses = ({
	statusType,
	cargoActionType,
	setStatusType,
	setCargoActionType,
}: {
	statusType: StatusType;
	cargoActionType: CallType | null;
	setStatusType: (type: StatusType) => void;
	setCargoActionType: (type: CallType | null) => void;
}) => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const { data, refetch, loading } = useQuery<GetAllStatusesQuery>(GetAllStatusesDocument);
	const { data: statusRuleTypes } = useQuery<GetStatusRuleTypesQuery>(GetStatusRuleTypesDocument);

	const [ saveStatuses ] = useMutation<SaveStatusesMutation>(SaveStatusesDocument);
	const [ deleteStatuses ] = useMutation<DeleteStatusMutation>(DeleteStatusDocument);

	const allStatuses = data?.allStatuses ?? [];

	const session = useStoreState(store => store.session);

	const [ statusSettingsDialogOpen, openStatusSettingsDialog ] = useState(false);
	const [ changeOrderDialogOpen, openChangeOrderDialog ] = useState(false);
	const [ bulkEditEnabled, enableBulkEdit ] = useState(false);

	const fields = getStatusFieldVisibility(statusType, session);

	const filterStatusCargoActionType = (status: StatusFragment) => {

		if (status.type !== StatusType.Cargo) {
			return true;
		}

		return status.cargoActionType === cargoActionType || cargoActionType === null;
	};

	const statuses = allStatuses
		.filter(status => status.type === statusType)
		.filter(filterStatusCargoActionType)
		.sort((x, y) => {
			const orderX = x.statusOrder ?? Number.MAX_SAFE_INTEGER;
			const orderY = y.statusOrder ?? Number.MAX_SAFE_INTEGER;
			return orderX - orderY;
		});

	const { isReadOnly } = usePermission();

	const { control, handleSubmit, reset } = useForm<{ statuses: StatusFragment[] }>({
		defaultValues: {
			statuses: [],
		},
	});

	const save = (data: { statuses: StatusFragment[] }) => {
		saveStatuses({
			variables: {
				inputs: data.statuses.map(status => convertStatusToMutationInput(status, statusRuleTypes?.ruleTypes)),
			},
		}).then(() => {
			refetch();
			enableBulkEdit(false);
		});
	};

	const deleteStatus = (status: StatusFragment) => {
		deleteStatuses({
			variables: {
				id: status.id,
			},
		}).then(() => refetch());
	};

	const icon = (checked: boolean) => {
		return checked ? <DoneIcon fontSize="small" color="success" /> : <CloseIcon fontSize="small" color="error" />;
	};

	useEffect(() => {
		reset({
			statuses: statuses,
		});
	}, [ bulkEditEnabled ]);

	const columns: Array<{
		title: ReactNode;
		enabled: boolean;
		render: (status: StatusFragment) => ReactNode;
		formFieldId?: OnlyStatusFlags;
	}> = [ {
		title: intl.formatMessage({ id: "STATUSES_REQUIRE_ACTUAL_TIME" }),
		enabled: fields.requireActualTime,
		render: (status: StatusFragment) => icon(status.requireActualTime !== StatusActualTimeType.NotRequired),
	}, {
		title: intl.formatMessage({ id: "STATUSES_REQUIRE_ESTIMATED_TIME" }),
		enabled: fields.estimateType,
		render: (status: StatusFragment) => icon(status.estimateRequirement === StatusEstimateRequirement.Required ),
	}, {
		title: intl.formatMessage({ id: "STATUS_REQUIRE_DOCUMENTS" }),
		enabled: fields.requireDocuments,
		render: (status: StatusFragment) => icon(status.requireDocuments),
		formFieldId: "requireDocuments",
	}, {
		title: intl.formatMessage({ id: "STATUS_SEND_DOCUMENT" }),
		enabled: fields.sendDocument,
		render: (status: StatusFragment) => icon(status.sendDocument?.enabled ?? false),
	}, {
		title: intl.formatMessage({ id: "STATUSES_MEMO" }),
		enabled: fields.allowComment,
		render: (status: StatusFragment) => icon(status.allowComment),
		formFieldId: "allowComment",
	}, {
		title: intl.formatMessage({ id: "ALLOW_SKIPPER_COMMENT" }),
		enabled: fields.allowSkipperComment,
		render: (status: StatusFragment) => icon(status.allowSkipperComment),
		formFieldId: "allowSkipperComment",
	}, {
		title: intl.formatMessage({ id: "STATUS_IN_PROGRESS" }),
		enabled: fields.isInProgress,
		render: (status: StatusFragment) => icon(status.isInProgress),
		formFieldId: "isInProgress",
	}, {
		title: intl.formatMessage({ id: "STATUSES_FINISHED_STATUS" }),
		enabled: fields.finishedStatus,
		render: (status: StatusFragment) => icon(status.finishedStatus),
		formFieldId: "finishedStatus",
	}, {
		title: intl.formatMessage({ id: "STATUSES_CLOSED_STATUS" }),
		enabled: fields.closedStatus,
		render: (status: StatusFragment) => icon(status.closedStatus),
		formFieldId: "closedStatus",
	}, {
		title: intl.formatMessage({ id: "STATUSES_CANCELLED_STATUS" }),
		render: (status: StatusFragment) => icon(status.cancelledStatus),
		enabled: fields.cancelledStatus,
		formFieldId: "cancelledStatus",
	}, {
		title: intl.formatMessage({ id: "STATUSES_EXPIRING_STATUS" }),
		enabled: fields.expiringStatus,
		render: (status: StatusFragment) => icon(status.expiringStatus),
		formFieldId: "expiringStatus",
	}, {
		title: intl.formatMessage({ id: "STATUS_APPLY_BY_CUSTOMER" }),
		enabled: fields.applyByCustomer,
		render: (status: StatusFragment) => icon(status.applyByCustomer),
		formFieldId: "applyByCustomer",
	}, {
		title: intl.formatMessage({ id: "STATUS_APPLY_BY_SKIPPER" }),
		enabled: fields.applyBySkipper,
		render: (status: StatusFragment) => icon(status.applyBySkipper),
		formFieldId: "applyBySkipper",
	}, {
		title: intl.formatMessage({ id: "STATUS_APPLY_BY_TERMINAL" }),
		enabled: fields.applyByTerminal,
		render: (status: StatusFragment) => icon(status.applyByTerminal),
		formFieldId: "applyByTerminal",
	}, {
		title: intl.formatMessage({ id: "STATUSES_LOADED_STATUS" }),
		enabled: fields.loadedStatus,
		render: (status: StatusFragment) => icon(status.loadedStatus),
		formFieldId: "loadedStatus",
	}, {
		title: intl.formatMessage({ id: "STATUSES_DISCHARGED_STATUS" }),
		enabled: fields.dischargedStatus,
		render: (status: StatusFragment) => icon(status.dischargedStatus),
		formFieldId: "dischargedStatus",
	}, {
		title: intl.formatMessage({ id: "VOYAGE_VISIBLE_FOR_SKIPPER" }),
		enabled: fields.voyageVisibleBySkipper,
		render: (status: StatusFragment) => icon(status.voyageVisibleBySkipper),
		formFieldId: "voyageVisibleBySkipper",
	}, {
		title: intl.formatMessage({ id: "VOYAGE_VISIBLE_FOR_CREDITOR" }),
		enabled: fields.voyageVisibleByCreditor,
		render: (status: StatusFragment) => icon(status.voyageVisibleByCreditor),
		formFieldId: "voyageVisibleByCreditor",
	}, {
		title: intl.formatMessage({ id: "VOYAGE_EDITABLE_BY_SKIPPER" }),
		enabled: fields.voyageEditableBySkipper,
		render: (status: StatusFragment) => icon(status.voyageEditableBySkipper),
		formFieldId: "voyageEditableBySkipper",
	}, {
		title: intl.formatMessage({ id: "VOYAGE_EDITABLE_BY_CREDITOR" }),
		enabled: fields.voyageEditableByCreditor,
		render: (status: StatusFragment) => icon(status.voyageEditableByCreditor),
		formFieldId: "voyageEditableByCreditor",
	}, {
		title: intl.formatMessage({ id: "CALL_VISABLE_FOR_LOCATION_OWNER" }),
		enabled: fields.callVisibleByLocationOwner,
		render: (status: StatusFragment) => icon(status.callVisibleByLocationOwner),
		formFieldId: "callVisibleByLocationOwner",
	}, {
		title: intl.formatMessage({ id: "VOYAGE_REFUSE" }),
		enabled: fields.isRejectStatus,
		render: (status: StatusFragment) => icon(status.isRejectStatus),
		formFieldId: "isRejectStatus",
	}, {
		title: intl.formatMessage({ id: "VOYAGE_ACCEPTED" }),
		enabled: fields.isAcceptStatus,
		render: (status: StatusFragment) => icon(status.isAcceptStatus),
		formFieldId: "isAcceptStatus",
	}, {
		title: intl.formatMessage({ id: "STATUS_NOTIFY_SHIP_OWNERS" }),
		enabled: fields.notifyShipOwners,
		render: (status: StatusFragment) => icon(status.notifyShipOwners),
		formFieldId: "notifyShipOwners",
	}, {
		title: intl.formatMessage({ id: "STATUS_NOTIFY_SKIPPERS" }),
		enabled: fields.notifySkippers,
		render: (status: StatusFragment) => icon(status.notifySkippers),
		formFieldId: "notifySkippers",
	}, {
		title: intl.formatMessage({ id: "STATUS_NOTIFY_SHIP_MANAGERS" }),
		enabled: fields.notifyShipManagers,
		render: (status: StatusFragment) => icon(status.notifyShipManagers),
		formFieldId: "notifyShipManagers",
	}, {
		title: intl.formatMessage({ id: "STATUS_NOTIFY_OPERATORS" }),
		enabled: fields.notifyOperators,
		render: (status: StatusFragment) => icon(status.notifyOperators),
		formFieldId: "notifyOperators",
	}, {
		title: intl.formatMessage({ id: "STATUS_NOTIFY_CUSTOMERS" }),
		enabled: fields.notifyCustomers,
		render: (status: StatusFragment) => icon(status.notifyCustomers),
		formFieldId: "notifyCustomers",
	}, {
		title: intl.formatMessage({ id: "STATUS_NOTIFY_TERMINALS" }),
		enabled: fields.notifyTerminals,
		render: (status: StatusFragment) => icon(status.notifyTerminals),
		formFieldId: "notifyTerminals",
	}, {
		title: intl.formatMessage({ id: "NOMINATE_TO_CUSTOMER" }),
		enabled: fields.isCustomerNominatingStatus,
		render: (status: StatusFragment) => icon(status.isCustomerNominatingStatus),
		formFieldId: "isCustomerNominatingStatus",
	}, {
		title: intl.formatMessage({ id: "NOMINATE_TO_SKIPPER" }),
		enabled: fields.isSkipperNominatingStatus,
		render: (status: StatusFragment) => icon(status.isSkipperNominatingStatus),
		formFieldId: "isSkipperNominatingStatus",
	}, {
		title: intl.formatMessage({ id: "STATUSES_REQUIRE_SUPPLIER" }),
		enabled: fields.supplierRequired,
		render: (status: StatusFragment) => icon(status.supplierRequired),
		formFieldId: "supplierRequired",
	}, {
		title: intl.formatMessage({ id: "STATUS_SAILING_REPORT" }),
		enabled: fields.skipperRequiredFillInSailingReport,
		render: (status: StatusFragment) => icon(status.skipperRequiredFillInSailingReport),
		formFieldId: "skipperRequiredFillInSailingReport",
	}, {
		title: intl.formatMessage({ id: "STATUS_WEIGHT_VOLUME" }),
		enabled: fields.skipperRequiredFillInWeightVolume,
		render: (status: StatusFragment) => icon(status.skipperRequiredFillInWeightVolume),
		formFieldId: "skipperRequiredFillInWeightVolume",
	}, {
		title: intl.formatMessage({ id: "STATUS_SHOULD_UPDATE_JDE" }),
		enabled: fields.shouldUpdateJDE,
		render: (status: StatusFragment) => icon(status.shouldUpdateJDE),
		formFieldId: "shouldUpdateJDE",
	}, {
		title: intl.formatMessage({ id: "STATUS_SHOULD_LOAD_ON_TERMINAL" }),
		enabled: fields.shouldLoadOnTerminal,
		render: (status: StatusFragment) => icon(status.shouldLoadOnTerminal),
		formFieldId: "shouldLoadOnTerminal",
	}, {
		title: intl.formatMessage({ id: "STATUS_INCLUDE_IN_NOMINATION_EXPORT" }),
		enabled: fields.includeInNominationExport,
		render: (status: StatusFragment) => icon(status.includeInNominationExport),
		formFieldId: "includeInNominationExport",
	}, {
		title: intl.formatMessage({ id: "STATUS_LAYTIME_START" }),
		enabled: fields.laytimeStartStatus,
		render: (status: StatusFragment) => icon(status.laytimeStartStatus),
		formFieldId: "laytimeStartStatus",
	}, {
		title: intl.formatMessage({ id: "STATUS_LAYTIME_END" }),
		enabled: fields.laytimeEndStatus,
		render: (status: StatusFragment) => icon(status.laytimeEndStatus),
		formFieldId: "laytimeEndStatus",
	}, {
		title: intl.formatMessage({ id: "STATUS_REQUIRE_TANKS" }),
		enabled: fields.requireTanks,
		render: (status: StatusFragment) => icon(status.requireTanks),
		formFieldId: "requireTanks",
	} ];

	const additionalActionTypes = hasPluginPermission(session, "cargoActionUsePurgingAndCleaningTypes") ? [ CallType.Cleaning, CallType.NitrogenPurging ] : [];

	return (
		<div className="statuses">
			<PageHeader>
				<PageHeader.Title>
					{Language("STATUSES_TITLE")}
					<Helmet title={intl.formatMessage({ id: "STATUSES_TITLE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button
							onClick={() => navigate("/view/statuses/create")}>
							{intl.formatMessage({ id: "ADD" })}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<form onSubmit={handleSubmit(save)} id="statusesForm">
				<Card
					actions={isReadOnly ? undefined : [ {
						text: intl.formatMessage({ id: "SAVE" }),
						onClick: handleSubmit(save),
						hidden: !bulkEditEnabled,
					}, {
						text: intl.formatMessage({ id: "CANCEL" }),
						onClick: () => enableBulkEdit(false),
						hidden: !bulkEditEnabled,
					}, {
						text: `${intl.formatMessage({ id: "EDIT" })} ${intl.formatMessage({ id: "STATUSES_TITLE" })}`,
						onClick: () => enableBulkEdit(true),
						hidden: bulkEditEnabled,
					}, {
						text: intl.formatMessage({ id: "CHANGE_ORDER" }),
						onClick: () => openChangeOrderDialog(true),
						hidden: bulkEditEnabled,
					}, {
						text: intl.formatMessage({ id: "STATUS_SETTINGS" }),
						onClick: () => openStatusSettingsDialog(true),
						hidden: bulkEditEnabled,
					} ]}>
					<div className="table-container">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell />

									<TableCell style={{ minWidth: 250, maxWidth: 250, width: 250 }}>
										<FormGroup label={intl.formatMessage({ id: "SELECT_TYPE" })} overflow="hidden">
											<Grid container>
												<Grid size={statusType === StatusType.Cargo ? 6 : 12} style={{ paddingRight: 12 }}>
													<StatusTypeSelect
														disabled={bulkEditEnabled}
														value={statusType}
														onChange={option => {
															if (option) {
																setStatusType(option);
															}
														}} />
												</Grid>
												{statusType === StatusType.Cargo && (
													<Grid size={6}>
														<CallTypeSelect
															disabled={bulkEditEnabled}
															additionalOptions={additionalActionTypes}
															value={cargoActionType}
															clearable={true}
															onChange={option => {
																setCargoActionType(option);
															}}
														/>
													</Grid>
												)}
											</Grid>
										</FormGroup>
									</TableCell>

									{columns.filter(column => column.enabled).map((column, index) => (
										<RotatedCell title={column.title} key={index}>
											{column.title}
										</RotatedCell>
									))}

									<TableCell />
									<TableCell />
								</TableRow>
							</TableHead>

							<TableBody>
								{statuses.length > 0 ? statuses.map((status, index) => (
									<TableRow key={index}>
										<ColorCell color={status.color} />

										<TableCell>
											{DataFormat.status(status, session.locale)}
										</TableCell>

										{columns.filter(column => column.enabled).map((column, columnIndex) => (
											<BooleanCell key={columnIndex}>
												{(bulkEditEnabled && column.formFieldId) ? (
													<Controller
														name={`statuses.${index}.${column.formFieldId}`}
														control={control}
														render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
												) : column.render(status)}
											</BooleanCell>
										))}

										<TableCell>
											{!isReadOnly && <span className="actions">
												<IconButton size="small" onClick={() => navigate(`/view/statuses/${status.id}/edit`)} disabled={bulkEditEnabled}>
													<EditIcon />
												</IconButton>

												<IconButton
													size="small"
													disabled={bulkEditEnabled}
													onClick={() => {
														confirm({
															type: "DELETE",
														}).then(() => {
															deleteStatus(status);
														});
													}}>
													<DeleteIcon />
												</IconButton>
											</span>}
										</TableCell>

										<ColorCell color={status.color} />
									</TableRow>
								)) : (
									<TableRow>
										<TableCell colSpan={100}>
											{Language("TABLE_BODY_EMPTY")}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>

					{(loading) && (
						<div className="loading">
							<CircularProgress size={24} />
						</div>
					)}
				</Card>
			</form>

			<DefaultStatusSettingsDialog
				open={statusSettingsDialogOpen}
				onClose={() => openStatusSettingsDialog(false)}
				statuses={allStatuses} />

			<ChangeStatusOrderDialog
				open={changeOrderDialogOpen}
				onClose={() => openChangeOrderDialog(false)}
				refetch={refetch}
				statuses={statuses} />
		</div>
	);
};

const ColorCell = ({ color }: { color: string }) => {
	return (
		<TableCell className="color-cell">
			<span style={{ backgroundColor: color }} />
		</TableCell>
	);
};

const RotatedCell = ({ title, children }: { title: ReactNode, children: ReactNode }) => {
	return (
		<TableCell className="rotated-cell">
			<Tooltip title={title}>
				<div>
					<div>
						<span>
							{children}
						</span>
					</div>
				</div>
			</Tooltip>
		</TableCell>
	);
};

const BooleanCell = ({ children }: { children: ReactNode }) => {
	return (
		<TableCell className="boolean-cell">
			<div>
				{children}
			</div>
		</TableCell>
	);
};

export default Statuses;
